import React, { useCallback, useMemo, memo, useEffect } from 'react';
import MUIDataTable from "mui-datatables";
import { ThemeProvider, makeStyles } from '@material-ui/core/styles';
import theme from 'theme';
import { TableCell } from '@material-ui/core'
import { useTheme, useMediaQuery, Typography, Card, CardContent, Grid } from '@material-ui/core'
import { ReactComponent as IconSort } from 'assets/svgs/sort.svg';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import _ from 'lodash';

// Move makeStyles outside component to prevent recreation on every render
// Use CSS variables for dynamic startIndex to keep styles static
const useStyles = makeStyles(theme => ({
        root: {
            '& table': {
                '& thead th': {
                    textAlign: 'left',
                    color: '#6E7697',
                    padding: `${theme.spacing(1, 2)} !important`,
                    borderBottom: 'none',
                    fontSize: 12,
                    lineHeight: '14px',
                    backgroundColor: '#F2F5FB',
                    '&>span': {
                        justifyContent: 'flex-start'
                    }
                },
                '& tbody': {
                    '& tr': {
                        transition: '0.5s',
                        cursor: 'pointer',
                        '& td': {
                            fontSize: 12,
                            color: theme.palette.app.primary,
                            padding: `${theme.spacing(2, 2)} !important`,
                            textAlign: 'left',
                            backgroundColor: 'white',
                            // borderTop: '1px solid ' + theme.palette.app.outlineborder,
                            // borderBottom: '1px solid ' + theme.palette.app.outlineborder,
                            // '&:first-child': {
                            //     borderLeft: '1px solid ' + theme.palette.app.outlineborder,
                            //     borderTopLeftRadius: 10,
                            //     borderBottomLeftRadius: 10
                            // },
                            // '&:last-child': {
                            //     borderRight: '1px solid ' + theme.palette.app.outlineborder,
                            //     borderTopRightRadius: 10,
                            //     borderBottomRightRadius: 10
                            // },
                        },
                        '&:nth-child(even) td': {
                            backgroundColor: '#F8F9FD'
                        },
                        '&:first-child td': {
                            // Use CSS variable for dynamic nth-child selector
                            '&:nth-child(var(--start-index, 2))': {
                                borderTopLeftRadius: 15,
                                overflow: 'hidden'
                            },
                            '&:last-child': {
                                borderTopRightRadius: 15,
                                overflow: 'hidden'
                            },
                        },
                        '&:last-child td': {
                            '&:nth-child(var(--start-index, 2))': {
                                borderBottomLeftRadius: 15,
                                overflow: 'hidden'
                            },
                            '&:last-child': {
                                borderBottomRightRadius: 15,
                                overflow: 'hidden'
                            },
                        },
                        '&:hover': {
                            //backgroundColor: `${theme.palette.app.primary} !important`,
                            '& td': {
                                //color: 'white !important',
                                //fill: 'white !important'
                            }
                        },
                    },
                },
            },
        },
        button: {
            color: theme.palette.app.primary,
            cursor: 'pointer',
            whiteSpace: 'nowrap',
        },
        sortIcon: {
            marginLeft: 4,
            marginTop: 4,
            height: 12
        },
        fixedHeader: {
            position: "sticky",
            zIndex: 100,
            top: 0
        }
        ,
        cardList: {
            display: 'flex',
            flexDirection: 'column',
            gap: theme.spacing(2)
        },
        cardItem: {
            backgroundColor: '#FFFFFF',
            borderRadius: 8,
            // padding: theme.spacing(2),
            boxShadow: '0px 2px 6px rgba(0,0,0,0.06)',
            border: '1px solid #E6E9F0'
        },
        cardRowLabel: {
            color: '#6E7697',
            fontSize: 12
        },
        cardRowValue: {
            color: theme.palette.app.primary,
            fontSize: 14
        }
    }));

// Memoize theme overrides to prevent recreation on every render
const themeOverrides = {
    MuiToolbar: {
        root: {
            backgroundColor: "transparent"
        }
    },
    MUIDataTableFilterList: {
        root: {
            backgroundColor: "#FAFAFD",
            margin: 0
        }
    },
    MuiFormControl: {
        root: {
            backgroundColor: "#FAFAFD"
        }
    },
    MUIDataTableFilter: {
        root: {
            backgroundColor: "#FAFAFD"
        }
    },
    MuiSelect: {
        root: {
            backgroundColor: "#FAFAFD"
        }
    },
    MuiPaper: {
        root: {
            backgroundColor: 'transparent'
        }
    },
    MUIDataTableSelectCell: {
        root: {
            display: "none"
        }
    }
};

const CustomTable = (props) => {
    const classes = useStyles();
    const muiTheme = useTheme();
    const isXs = useMediaQuery(muiTheme.breakpoints.down('xs')) && !(props.responseflag === 'none');
    
    // Memoize startIndex and manage dynamic styles for nth-child selector
    const startIndex = props.startIndex || 2;
    const dynamicClassName = useMemo(() => `custom-table-root-${startIndex}`, [startIndex]);
    
    // Manage dynamic style injection for nth-child selectors
    // Guard against test environments where document might not be available
    useEffect(() => {
        // Skip style injection in test environments or when document is not available
        if (typeof document === 'undefined' || !document.head) {
            return () => {}; // Return empty cleanup function
        }
        
        const styleId = `custom-table-start-index-${startIndex}`;
        let styleElement = document.getElementById(styleId);
        let wasCreated = false;
        
        if (!styleElement) {
            styleElement = document.createElement('style');
            styleElement.id = styleId;
            document.head.appendChild(styleElement);
            wasCreated = true;
        }
        
        styleElement.textContent = `
            .${dynamicClassName} table tbody tr:first-child td:nth-child(${startIndex}) {
                border-top-left-radius: 15px;
                overflow: hidden;
            }
            .${dynamicClassName} table tbody tr:last-child td:nth-child(${startIndex}) {
                border-bottom-left-radius: 15px;
                overflow: hidden;
            }
        `;
        
        // Cleanup function to remove style when component unmounts or startIndex changes
        return () => {
            // Use try-catch to handle cases where document is cleaned up during tests
            try {
                if (typeof document !== 'undefined' && document.head && document.getElementById) {
                    const existingStyle = document.getElementById(styleId);
                    if (existingStyle && wasCreated) {
                        // Only remove if we created it
                        // In a real app with multiple instances, you'd want a ref counter
                        existingStyle.remove();
                    }
                }
            } catch (error) {
                // Silently fail in test environments where document may be cleaned up
            }
        };
    }, [startIndex, dynamicClassName]);

    // Memoize renderHeadCell to prevent recreation
    const renderHeadCell = useCallback(
        ({ index, ...column }, sortColumn, label, sortEnabled, fixedHeader) => (
            <TableCell key={index} onClick={() => sortColumn(index)} className={clsx(classes.button, fixedHeader && classes.fixedHeader)} endIcon={<IconSort />}>
                {label}
                {sortEnabled && <IconSort className={classes.sortIcon} />}
            </TableCell >
        ),
        [classes.button, classes.fixedHeader, classes.sortIcon]
    );

    // Memoize columns array to prevent unnecessary re-renders of MUIDataTable
    const columns = useMemo(() => {
        if (!props.columns || !Array.isArray(props.columns)) {
            return [];
        }
        return props.columns.map(item => ({
            ...item,
            options: {
                ...item.options,
                customHeadRender: ({ index, ...column }, sortColumn) =>
                    renderHeadCell({ index, ...column }, sortColumn, item.label, item.options?.sort, props.options?.fixedHeader),
            }
        }));
    }, [props.columns, props.options?.fixedHeader, renderHeadCell]);

    // Memoize theme object to prevent recreation
    const tableTheme = useMemo(() => ({
        ...theme,
        overrides: {
            ...theme.overrides,
            ...themeOverrides
        }
    }), []);

    // Memoize row data processing for mobile view
    const processRowData = useCallback((row, columns) => {
        return columns.map(c => {
            const f = c.name || c.key;
            if (f && f.includes('.')) {
                return _.get(row, f);
            }
            return row[f];
        });
    }, []);

    // Memoize visible columns for mobile view
    const visibleColumns = useMemo(() => {
        if (!props.columns || !Array.isArray(props.columns)) {
            return [];
        }
        return props.columns.filter(col => !(col.options && col.options.display === false));
    }, [props.columns]);

    // Memoize card row renderer
    const renderCardRow = useCallback((row, rowIndex, rowData) => {
        return visibleColumns.map((col, colIdx) => {
            const fieldName = col.name || col.key;
            const field = col.key || col.name;
            const label = col.label || field;
            
            // Get value from row - handle nested paths properly
            let value;
            if (fieldName && fieldName.includes('.')) {
                value = _.get(row, fieldName);
            } else {
                value = row[field];
            }
            
            // Use customBodyRender if provided
            if (col.options && col.options.customBodyRender) {
                const tableMeta = { rowIndex, rowData, columnIndex: colIdx };
                try {
                    value = col.options.customBodyRender(value, tableMeta);
                } catch (err) {
                    try {
                        value = col.options.customBodyRender(value, tableMeta, row);
                    } catch (err2) {
                        // fallback to raw value
                    }
                }
            }
            
            return (
                <Grid item xs={12} key={field}>
                    <Typography className={classes.cardRowLabel}>{label}</Typography>
                    <div className={classes.cardRowValue}>{value}</div>
                </Grid>
            );
        });
    }, [visibleColumns, classes.cardRowLabel, classes.cardRowValue]);

    // If small screen and responsive vertical requested, render cards with all columns
    if (isXs) {
        return (
            <section className={props.overwriteStyle ? props.containerStyle : clsx(classes.root, dynamicClassName, props.containerStyle)}>
                <div className={classes.cardList}>
                    {props.data && props.data.map((row, rowIndex) => {
                        // Pre-compute rowData once per row
                        const rowData = props.columns ? processRowData(row, props.columns) : [];
                        return (
                            <Card key={row.id || rowIndex} className={classes.cardItem}>
                                <CardContent>
                                    <Grid container spacing={1}>
                                        {renderCardRow(row, rowIndex, rowData)}
                                    </Grid>
                                </CardContent>
                            </Card>
                        );
                    })}
                </div>
            </section>
        );
    }

    return (
        <section className={props.overwriteStyle ? props.containerStyle : clsx(classes.root, dynamicClassName, props.containerStyle)}>
            <ThemeProvider theme={tableTheme}>
                <MUIDataTable {...props} columns={columns} />
            </ThemeProvider>
        </section>
    );
};
CustomTable.propTypes = {
    startIndex: PropTypes.any,
    columns: PropTypes.any,
    overwriteStyle: PropTypes.any,
    containerStyle: PropTypes.any,
    options: PropTypes.any,
    data: PropTypes.any,
    responseflag: PropTypes.any
};

// Memoize component to prevent unnecessary re-renders when props haven't changed
export default memo(CustomTable, (prevProps, nextProps) => {
    // Custom comparison function for better performance
    // Handle undefined/null props gracefully
    try {
        return (
            prevProps.columns === nextProps.columns &&
            prevProps.data === nextProps.data &&
            prevProps.options === nextProps.options &&
            prevProps.startIndex === nextProps.startIndex &&
            prevProps.containerStyle === nextProps.containerStyle &&
            prevProps.overwriteStyle === nextProps.overwriteStyle &&
            prevProps.responseflag === nextProps.responseflag
        );
    } catch (error) {
        // If comparison fails, allow re-render to be safe
        return false;
    }
});
